import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';



// Styled Components
const GlobalStyle = createGlobalStyle`
  body, html {
    margin: 0;
    padding: 0;
    height: 100%;
    background: linear-gradient(135deg, #0f1528 0%, #1a2b45 50%, #162a4c 100%);
    color: #e6eaf2;
    overflow-x: hidden;
  }
`;

const Container = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 100vh;
  padding: 15px;
  gap: 15px;
  background: linear-gradient(135deg, #0f1528 0%, #1a2b45 50%, #162a4c 100%);
  color: #e6eaf2;
  overflow: hidden;
  position: relative;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  background: rgba(16, 24, 48, 0.85);
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    background: linear-gradient(90deg, #4e80e1, #32e875);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 0 10px rgba(78, 128, 225, 0.3);
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const ModeSwitch = styled.div`
  display: flex;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  padding: 4px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 4px;
    left: ${props => props.mode === '3d' ? 'calc(50% + 4px)' : '4px'};
    width: calc(50% - 8px);
    height: calc(100% - 8px);
    background: #4e80e1;
    border-radius: 18px;
    transition: left 0.3s;
  }
`;

const ModeButton = styled.button`
  background: none;
  border: none;
  color: #e6eaf2;
  padding: 8px 15px;
  border-radius: 18px;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: color 0.3s;
  
  &.active {
    color: #1a2b45;
  }
`;

const SyncStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #8aa0c1;
`;

const Pulse = styled.div`
  width: 8px;
  height: 8px;
  background-color: #32e875;
  border-radius: 50%;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(50, 232, 117, 0.7);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(50, 232, 117, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(50, 232, 117, 0);
    }
  }
`;

const Main = styled.main`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 15px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }
`;

const MapContainer = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(16, 24, 48, 0.85);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  min-height: 600px; /* 添加最小高度 */
  display: flex; /* 使用 flex 布局 */
  flex-direction: column; /* 垂直方向排列 */
`;

const CollapsibleMapFilter = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(26, 43, 69, 0.9);
  border-radius: 10px;
  padding: ${props => props.collapsed ? '10px' : '15px'};
  z-index: 10;
  width: ${props => props.collapsed ? 'auto' : '250px'};
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    margin-bottom: 15px;
  }
`;

const FilterToggleButton = styled.button`
  background: rgba(78, 128, 225, 0.3);
  border: none;
  color: #e6eaf2;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(78, 128, 225, 0.5);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.div`
  font-size: 0.9rem;
  margin-bottom: 8px;
  color: #8aa0c1;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterBadge = styled.div`
  background: ${props => props.active ? props.activeColor || '#4e80e1' : 'rgba(78, 128, 225, 0.2)'};
  border: 1px solid ${props => props.borderColor || 'rgba(78, 128, 225, 0.3)'};
  color: ${props => props.active && props.activeColor ? '#0f1528' : props.color || '#e6eaf2'};
  border-radius: 15px;
  padding: 4px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 0 10px rgba(78, 128, 225, 0.5)' : 'none'};
  
  &:hover {
    background: ${props => props.active ? props.activeColor || '#4e80e1' : 'rgba(78, 128, 225, 0.3)'};
  }
`;

const Map = styled.div`
  width: 100%;
  height: 100%;
  min-height: 550px;
  background: url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9IiMyYTNhNTYiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==');
  position: relative;
  overflow: hidden;
  transition: all 0.5s ease-out;
  perspective: ${props => props.mode === '3d' ? '1000px' : 'none'};
  flex: 1;
  cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
  touch-action: none; /* 防止觸控事件被瀏覽器處理 */
`;

const FloorPlan = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: ${props => `
    translate(
      calc(-50% + ${props.position.x}px), 
      calc(-50% + ${props.position.y}px)
    ) 
    scale(${props.zoomLevel})
    ${props.mode === '3d' ? 'rotateX(45deg)' : ''}
  `};
  width: 95%;
  height: 95%;
  background: rgba(42, 58, 86, 0.4);
  border: 2px solid ${props => props.mode === '3d'
    ? 'rgba(78, 128, 225, 0.5)'
    : 'rgba(78, 128, 225, 0.3)'};
  border-radius: 5px;
  transition: transform 0.3s;
  box-shadow: ${props => props.mode === '3d' ? '0 10px 30px rgba(0, 0, 0, 0.3)' : 'none'};
  overflow: visible;
`;

// 在地圖中添加樓層標籤
const FloorLabel = styled.div`
  position: absolute;
  left: 5px;
  color: #8aa0c1;
  font-size: 0.8rem;
  font-weight: bold;
  z-index: 3;
`;

const Room = styled.div`
  position: absolute;
  width: ${props => props.activeFloor === 'all' ? props.width * 0.7 : props.width * 1.8}%;
  height: ${props => props.activeFloor === 'all' ? props.height * 0.7 : props.height * 1.8}%;
  left: ${props => props.x}%;
  top: ${props => props.y}%;
  background: ${props => props.occupied ? 'rgba(255, 51, 85, 0.2)' : 'rgba(50, 232, 117, 0.2)'};
  border: 1px solid ${props => props.occupied ? 'rgba(255, 51, 85, 0.5)' : 'rgba(50, 232, 117, 0.5)'};
  border-radius: 3px;
  transition: all 0.3s;
  cursor: pointer;
  z-index: ${props => props.isHovered ? 10 : 1};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  margin: 2px;
  padding: 2px;
  
  &:hover {
    background: ${props => props.occupied ? 'rgba(255, 51, 85, 0.3)' : 'rgba(50, 232, 117, 0.3)'};
    transform: scale(1.1);
    z-index: 10;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }
`;

const RoomLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.activeFloor === 'all' ? '0.6rem' : '0.8rem'};
  font-weight: bold;
  color: #e6eaf2;
  white-space: nowrap;
  text-shadow: 0 0 3px #000000;
  letter-spacing: -0.5px;
  line-height: 1;
  text-align: center;
  opacity: ${props => props.showLabel ? '1' : '0'};
  transition: opacity 0.3s;
  
  /* 在小屏幕上優化顯示 */
  @media (max-width: 768px) {
    font-size: ${props => props.activeFloor === 'all' ? '0.5rem' : '0.7rem'};
  }
`;

const RoomTooltip = styled.div`
  position: absolute;
  background: rgba(26, 43, 69, 0.9);
  color: #e6eaf2;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  top: ${props => props.y}px;
  left: ${props => props.x}px;
  transition: opacity 0.2s;
  z-index: 5;
`;

const DetailsPanel = styled.div`
  background: rgba(16, 24, 48, 0.85);
  border-radius: 10px;
  overflow: auto;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(10px);
  height: 100%;
  max-height: calc(100vh - 130px);
`;

const DetailsHeader = styled.div`
  padding: 15px;
  background: rgba(78, 128, 225, 0.1);
  border-bottom: 1px solid rgba(78, 128, 225, 0.3);
`;

const DetailsTitle = styled.div`
  font-size: 1.2rem;
  margin-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RoomId = styled.div`
  font-size: 0.85rem;
  color: #8aa0c1;
`;

const EmptyState = styled.div`
  flex: 1;
  display: ${props => props.visible ? 'flex' : 'none'};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  text-align: center;
  color: #8aa0c1;
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    opacity: 0.5;
  }
`;

const RoomDetails = styled.div`
  flex: 1;
  padding: 15px;
  overflow-y: auto;
  display: ${props => props.visible ? 'block' : 'none'};
  height: calc(100% - 60px); // 減去 header 高度和 padding
`;

const DetailsSection = styled.div`
  margin-bottom: 20px;
`;

const DetailsSectionTitle = styled.div`
  font-size: 0.9rem;
  color: #8aa0c1;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.div`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${props => props.occupied ? 'rgba(255, 51, 85, 0.2)' : 'rgba(50, 232, 117, 0.2)'};
  color: ${props => props.occupied ? '#ff3355' : '#32e875'};
`;

const DataCard = styled.div`
  background: rgba(78, 128, 225, 0.1);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DataLabel = styled.span`
  color: #8aa0c1;
  font-size: 0.85rem;
`;

const DataValue = styled.span`
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 15px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  margin-right: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #357ae8;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ChartContainer = styled.div`
  margin-top: 15px;
  background: rgba(26, 43, 69, 0.4);
  border-radius: 8px;
  padding: 10px;
  height: 200px;
  position: relative;
  // overflow: hidden;
`;

const ChartTitle = styled.h3`
  font-size: 0.9rem;
  color: #8aa0c1;
  margin-bottom: 8px;
  text-align: center;
`;

const DateTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;
`;

const DateTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DateTimeLabel = styled.label`
  font-size: 0.85rem;
  color: #8aa0c1;
  width: 80px;
`;

const DateTimeInput = styled.input`
  background: rgba(78, 128, 225, 0.1);
  border: 1px solid rgba(78, 128, 225, 0.3);
  border-radius: 4px;
  padding: 5px 8px;
  color: #e6eaf2;
  flex: 1;
  
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
`;

const RelaxationScoreContainer = styled.div`
  text-align: center;
  margin-top: 15px;
  padding: 10px;
  background: rgba(26, 43, 69, 0.4);
  border-radius: 8px;
`;

const RelaxationScoreTitle = styled.h3`
  font-size: 0.9rem;
  color: #8aa0c1;
  margin-bottom: 5px;
`;

const RelaxationScore = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #32e875;
`;

const FloorSelector = styled.div`
  position: absolute;
  right: 15px;
  top: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 5;
`;

const FloorButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.active ? '#4e80e1' : 'rgba(26, 43, 69, 0.7)'};
  color: #e6eaf2;
  border: 1px solid ${props => props.active ? '#4e80e1' : 'rgba(78, 128, 225, 0.3)'};
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: ${props => props.active ? '#4e80e1' : 'rgba(78, 128, 225, 0.5)'};
  }
`;

const ZoomControls = styled.div`
  position: absolute;
  right: 15px;
  bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 5;
`;

const ZoomButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(26, 43, 69, 0.7);
  color: #e6eaf2;
  border: 1px solid rgba(78, 128, 225, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: rgba(78, 128, 225, 0.5);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpButton = styled.button`
  position: absolute;
  left: 15px;
  bottom: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(26, 43, 69, 0.7);
  color: #e6eaf2;
  border: 1px solid rgba(78, 128, 225, 0.3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
  
  &:hover {
    background: rgba(78, 128, 225, 0.5);
  }
`;

const HelpOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 15, 30, 0.85);
  z-index: 20;
  display: ${props => props.show ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #e6eaf2;
`;

const HelpContent = styled.div`
  max-width: 400px;
  background: rgba(26, 43, 69, 0.9);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  
  h3 {
    margin-top: 0;
    color: #4e80e1;
  }
  
  ul {
    padding-left: 20px;
    margin-bottom: 20px;
  }
  
  button {
    background: #4e80e1;
    color: #fff;
    border: none;
    padding: 8px 15px;
    border-radius: 5px;
    cursor: pointer;
    width: 100%;
    
    &:hover {
      background: #3a6bc8;
    }
  }
`;

// 加載指示器
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(16, 24, 48, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 30;
  color: #e6eaf2;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid rgba(78, 128, 225, 0.3);
  border-radius: 50%;
  border-top-color: #4e80e1;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// 初始快速指南
const InitialHelpOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(10, 15, 30, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 25;
`;

const InitialHelpCard = styled.div`
  background: rgba(26, 43, 69, 0.95);
  border-radius: 10px;
  padding: 20px;
  max-width: 450px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  
  h3 {
    margin-top: 0;
    color: #4e80e1;
    text-align: center;
  }
  
  p {
    margin-bottom: 20px;
  }
  
  .tips {
    display: flex;
    justify-content: space-around;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }
  
  .tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 10px;
    width: 100px;
    text-align: center;
  }
  
  .tip-icon {
    width: 50px;
    height: 50px;
    background: rgba(78, 128, 225, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }
  
  .tip-label {
    font-size: 0.8rem;
    color: #8aa0c1;
  }
  
  button {
    background: #4e80e1;
    color: #fff;
    border: none;
    padding: 10px;
    border-radius: 5px;
    cursor: pointer;
    width: 100%;
    
    &:hover {
      background: #3a6bc8;
    }
  }
`;

// 樓層分組視圖組件
const FloorGroupView = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  padding: 20px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const FloorCard = styled.div`
  background: rgba(26, 43, 69, 0.7);
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${props => props.selected ? '#4e80e1' : 'rgba(78, 128, 225, 0.3)'};
  
  &:hover {
    background: rgba(26, 43, 69, 0.9);
    transform: translateY(-3px);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }
`;

const FloorCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const FloorNumber = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: #e6eaf2;
`;

const RoomCount = styled.div`
  font-size: 0.8rem;
  background: rgba(78, 128, 225, 0.2);
  padding: 3px 8px;
  border-radius: 10px;
  color: #8aa0c1;
`;

const RoomPreview = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const RoomIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${props => props.occupied ? 'rgba(255, 51, 85, 0.3)' : 'rgba(50, 232, 117, 0.3)'};
  border: 1px solid ${props => props.occupied ? 'rgba(255, 51, 85, 0.5)' : 'rgba(50, 232, 117, 0.5)'};
`;

const ViewToggleButton = styled.button`
  position: absolute;
  right: 15px;
  top: 80px;
  background: rgba(26, 43, 69, 0.7);
  color: #e6eaf2;
  border: 1px solid rgba(78, 128, 225, 0.3);
  padding: 8px 12px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 5;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(78, 128, 225, 0.5);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SleepReportButton = styled(Button)`
  background: ${props => props.primary ? '#4e80e1' : 'rgba(78, 128, 225, 0.2)'};
  &:hover {
    background: ${props => props.primary ? '#3a6bc8' : 'rgba(78, 128, 225, 0.3)'};
  }
`;

const SleepReportSection = styled.div`
  margin-top: 20px;
  border-top: 1px solid rgba(78, 128, 225, 0.3);
  padding-top: 20px;
`;

const ReportPreview = styled.div`
  margin-top: 15px;
  position: relative;
`;

const ReportTextArea = styled.textarea`
  width: 90%;
  height: 300px;
  background: rgba(26, 43, 69, 0.4);
  border: 1px solid rgba(78, 128, 225, 0.3);
  border-radius: 8px;
  padding: 12px;
  color: #e6eaf2;
  font-family: 'Courier New', Courier, monospace;
  font-size: 14px;
  line-height: 1.6;
  resize: vertical;
  white-space: pre-wrap;
  
  &:focus {
    outline: none;
    border-color: #4e80e1;
  }
`;

const CopyButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${props => props.copied ? 'rgba(50, 232, 117, 0.8)' : 'rgba(78, 128, 225, 0.8)'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.copied ? 'rgba(50, 232, 117, 1)' : 'rgba(78, 128, 225, 1)'};
  }
`;

const UserNameInput = styled.input`
  width: 90%;
  background: rgba(78, 128, 225, 0.1);
  border: 1px solid rgba(78, 128, 225, 0.3);
  border-radius: 4px;
  padding: 8px 12px;
  color: #e6eaf2;
  margin-top: 5px;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    border-color: #4e80e1;
  }
`;

const ReportLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  color: #8aa0c1;
  margin-bottom: 5px;
`;

const ErrorContainer = styled.div`
  padding: 10px;
  background: rgba(255, 51, 85, 0.1);
  border: 1px solid rgba(255, 51, 85, 0.3);
  border-radius: 5px;
  margin-bottom: 10px;
  color: #ff3355;
  font-size: 0.9rem;
`;

const LoadingIndicator = styled.div`
  font-size: 0.8rem;
  margin-left: auto;
  color: #8aa0c1;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: #8aa0c1;
  font-size: 0.9rem;
`;

const SchedulesList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const ScheduleItem = styled.div`
  padding: 10px;
  border-bottom: 1px solid rgba(78, 128, 225, 0.2);
  margin-bottom: 10px;
  position: relative;
`;

const StatusBadge_scheduling = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${props =>
    props.isActive
      ? 'rgba(50, 232, 117, 0.2)'
      : props.isCompleted
        ? 'rgba(133, 133, 133, 0.2)'
        : 'rgba(78, 128, 225, 0.2)'
  };
  border: 1px solid ${props =>
    props.isActive
      ? 'rgba(50, 232, 117, 0.5)'
      : props.isCompleted
        ? 'rgba(133, 133, 133, 0.5)'
        : 'rgba(78, 128, 225, 0.5)'
  };
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 0.75rem;
  color: ${props =>
    props.isActive
      ? '#32e875'
      : props.isCompleted
        ? '#999'
        : '#4e80e1'
  };
`;

const InfoRow = styled.div`
  margin-bottom: 5px;
`;

const Label = styled.span`
  display: inline-block;
  min-width: 100px;
  font-size: 0.85rem;
  color: #8aa0c1;
`;

const Value = styled.span`
  font-size: 0.9rem;
`;

const ScentValue = styled(Value)`
  font-weight: bold;
`;

const LoadButton = styled.button`
  background: rgba(78, 128, 225, 0.2);
  border: 1px solid rgba(78, 128, 225, 0.3);
  border-radius: 4px;
  padding: 6px 12px;
  color: #e6eaf2;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  
  &:hover {
    background: rgba(78, 128, 225, 0.3);
  }
`;

// 控制區域樣式
const ControlSection = styled.div`
  margin-top: 20px;
  padding: 15px;
  border-top: 1px solid #eee;
`;

const ControlTitle = styled.h4`
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #333;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;

const ControlLabel = styled.label`
  width: 120px;
  font-size: 14px;
  color: #666;
`;

const ControlSelectWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 10px;
`;

const ControlSelect = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-right: 10px;
  width: 120px;
`;

const ControlInputWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 10px;
`;

const ControlInput = styled.input`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-right: 10px;
  width: 120px;
`;

const ControlButtonRow = styled.div`
  display: flex;
  margin-top: 15px;
  gap: 10px;
  justify-content: space-between;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

// New futuristic control panel components
const DeviceControlPanel = styled.div`
  margin-top: 25px;
  background: rgba(16, 24, 48, 0.7);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(78, 128, 225, 0.3);
`;

const ControlPanelHeader = styled.h3`
  color: #4e80e1;
  margin: 0 0 15px 0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlGroupContainer = styled.div`
  margin-bottom: 15px;
`;

const ControlGroupLabel = styled.label`
  display: block;
  color: #8aa0c1;
  font-size: 0.85rem;
  margin-bottom: 8px;
`;

const ControlFlexRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const FuturisticSelect = styled.select`
  flex: 1;
  background: rgba(26, 43, 69, 0.8);
  border: 1px solid rgba(78, 128, 225, 0.5);
  border-radius: 6px;
  padding: 10px 15px;
  color: #e6eaf2;
  font-size: 0.8rem;
  outline: none;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.2);
  
  &:focus {
    border-color: #4e80e1;
  }
`;

const FuturisticInput = styled.div`
  flex: 1;
  background: rgba(26, 43, 69, 0.8);
  border: 1px solid rgba(78, 128, 225, 0.5);
  border-radius: 6px;
  padding: 10px 15px;
  position: relative;
  
  input {
    width: 100%;
    background: transparent;
    border: none;
    color: #e6eaf2;
    font-size: 0.9rem;
    outline: none;
  }
`;

const InputSuffix = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #8aa0c1;
  font-size: 0.8rem;
`;

const ControlButton = styled.button`
  background: ${props => props.disabled ? 'rgba(78, 128, 225, 0.3)' : props.variant === 'danger' ? 'rgba(255, 51, 85, 0.2)' : props.variant === 'success' ? 'rgba(50, 232, 117, 0.2)' : 'rgba(78, 128, 225, 0.8)'};
  color: ${props => props.variant === 'danger' ? '#ff3355' : props.variant === 'success' ? '#32e875' : '#e6eaf2'};
  padding: 10px 15px;
  border-radius: 6px;
  border: ${props => props.variant === 'danger' ? '1px solid rgba(255, 51, 85, 0.4)' : props.variant === 'success' ? '1px solid rgba(50, 232, 117, 0.4)' : 'none'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 120px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: ${props => props.disabled ? 0.7 : 1};
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'danger' ? 'rgba(255, 51, 85, 0.3)' : props.variant === 'success' ? 'rgba(50, 232, 117, 0.3)' : 'rgba(78, 128, 225, 1)'};
    transform: translateY(-1px);
  }
`;

const ControlGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 15px;
`;

const HideButton = styled.button`
  width: 100%;
  background: rgba(26, 43, 69, 0.8);
  border: 1px solid rgba(78, 128, 225, 0.3);
  border-radius: 6px;
  padding: 10px;
  color: #8aa0c1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(26, 43, 69, 1);
    color: #e6eaf2;
  }
`;

// 主要組件
const HotelRoomStatusPage = () => {
  const [mode, setMode] = useState('2d');
  const [rooms, setRooms] = useState([]);  // 移到這裡
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });
  const [filters, setFilters] = useState({
    status: { occupied: true, vacant: true },
    hotel: 'all',
    floor: 'all'
  });
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [deviceMappings, setDeviceMappings] = useState({});
  const [roomPositions, setRoomPositions] = useState({}); // 移到這裡
  const [floorsList, setFloorsList] = useState([]);

  // Aromapodpro device schedules
  const [scheduleData, setScheduleData] = useState([]);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [showSchedules, setShowSchedules] = useState(false);
  const [selectedAroma, setSelectedAroma] = useState('');
  const [intensityValue, setIntensityValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // 健康數據相關的狀態
  const [rrData, setRrData] = useState([]);
  const [hrData, setHrData] = useState([]);
  const [filteredRrData, setFilteredRrData] = useState([]);
  const [filteredHrData, setFilteredHrData] = useState([]);
  const [relaxationScore, setRelaxationScore] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  // 添加樓層選擇狀態
  const [activeFloor, setActiveFloor] = useState('all'); // 'all' 或特定樓層號如 '6', '7', '8', '9'
  const [zoomLevel, setZoomLevel] = useState(1); // 用於縮放控制
  const [mapPosition, setMapPosition] = useState({ x: 0, y: 0 }); // 用於平移控制

  // 添加到 HotelRoomStatusPage 組件內
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchDistance, setTouchDistance] = useState(null);
  const [hoveredRoom, setHoveredRoom] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showInitialHelp, setShowInitialHelp] = useState(false);

  // 添加樓層分組視圖狀態
  const [useFloorGroupView, setUseFloorGroupView] = useState(false);
  const [sleepReport, setSleepReport] = useState('');
  const [userName, setUserName] = useState('');
  const [reportCopied, setReportCopied] = useState(false);
  const [showSleepReport, setShowSleepReport] = useState(false);
  const [sleepData, setSleepData] = useState(null);

  // 自動決定是否使用樓層分組視圖
  useEffect(() => {
    // 如果房間數量超過臨界值，啟用樓層分組視圖
    if (rooms.length > 300) {
      setUseFloorGroupView(true);
    }
  }, [rooms]);

  // 過濾房間
  const filteredRooms = rooms.filter(room => {
    const isOccupied = !!room.guestDetails;
    const statusMatch = (isOccupied && filters.status.occupied) || (!isOccupied && filters.status.vacant);
    const hotelMatch = filters.hotel === room.hotelName;

    // Floor filtering (from room number)
    const roomFloor = room.roomNumber.charAt(0) + 'F';
    const floorMatch = filters.floor === 'all' || filters.floor === roomFloor;

    return statusMatch && hotelMatch && floorMatch;
  });

  // 修改房間過濾邏輯，優化大規模顯示
  const getDisplayRooms = () => {
    const totalRooms = filteredRooms.length;

    // 根據房間總數和縮放級別進行優化
    if (totalRooms > 500) {
      // 大規模房間顯示策略
      if (activeFloor === 'all') {
        if (zoomLevel < 0.8) {
          // 在低縮放級別下，使用抽樣顯示策略
          const samplingRate = Math.max(1, Math.floor(totalRooms / 100));
          return filteredRooms.filter((_, index) => index % samplingRate === 0);
        } else if (zoomLevel < 1.2) {
          // 中等縮放級別，顯示更多房間但仍然做一些抽樣
          const samplingRate = Math.max(1, Math.floor(totalRooms / 300));
          return filteredRooms.filter((_, index) => index % samplingRate === 0);
        }
      }
    }

    // 如果選擇了特定樓層，則只顯示該樓層的房間
    if (activeFloor !== 'all') {
      return filteredRooms.filter(room => parseInt(room.roomNumber.charAt(0)) === parseInt(activeFloor));
    }

    // 默認情況下顯示所有過濾後的房間
    return filteredRooms;
  };

  // 優化房間渲染效率的函數
  const optimizedRoomsRenderer = () => {
    const displayRooms = getDisplayRooms();

    // Don't try to optimize rendering when zoomed in significantly - show all items
    if (zoomLevel > 1.2) {
      return displayRooms.map((room) => {
        const pos = roomPositions[room.hotelName]?.[room.roomNumber];
        const position = pos || {
          x: 20 + (Math.random() * 60),
          y: 20 + (Math.random() * 60),
          width: 8,
          height: 8
        };

        const roomKey = `${room.hotelName}-${room.roomNumber}`;

        return (
          <Room
            key={roomKey}
            x={position.x}
            y={position.y}
            width={position.width}
            height={position.height}
            occupied={!!room.guestDetails}
            activeFloor={activeFloor}
            isHovered={hoveredRoom === roomKey}
            onClick={() => setSelectedRoom(room)}
            onMouseEnter={(e) => {
              handleRoomMouseEnter(room, e);
              setHoveredRoom(roomKey);
            }}
            onMouseLeave={() => {
              handleRoomMouseLeave();
              setHoveredRoom(null);
            }}
            onMouseMove={handleRoomMouseMove}
          >
            <RoomLabel
              activeFloor={activeFloor}
              showLabel={showLabels || hoveredRoom === roomKey}
            >
              {room.roomNumber}
              <span style={{
                fontSize: activeFloor === 'all' ? '0.4rem' : '0.5rem',
                marginTop: '2px',
                opacity: 0.8
              }}>
                {room.hotelName.split(' ')[0]}
              </span>
            </RoomLabel>
          </Room>
        );
      });
    }

    // For lower zoom levels, use optimization with viewport calculations
    const viewportWidth = mapRef.current ? mapRef.current.clientWidth : 1000;
    const viewportHeight = mapRef.current ? mapRef.current.clientHeight : 800;

    // Use a more generous buffer to prevent items from disappearing
    const bufferFactor = 2.5;

    // Adjusted viewport boundary calculation
    const minX = -mapPosition.x / zoomLevel - (viewportWidth * bufferFactor) / (2 * zoomLevel);
    const maxX = -mapPosition.x / zoomLevel + (viewportWidth * bufferFactor) / (2 * zoomLevel);
    const minY = -mapPosition.y / zoomLevel - (viewportHeight * bufferFactor) / (2 * zoomLevel);
    const maxY = -mapPosition.y / zoomLevel + (viewportHeight * bufferFactor) / (2 * zoomLevel);

    return displayRooms.map((room) => {
      const pos = roomPositions[room.hotelName]?.[room.roomNumber];
      const position = pos || {
        x: 20 + (Math.random() * 60),
        y: 20 + (Math.random() * 60),
        width: 8,
        height: 8
      };

      // More generous check to avoid items disappearing at edges
      if (position.x < minX - 20 || position.x > maxX + 20 ||
        position.y < minY - 20 || position.y > maxY + 20) {
        return null;
      }

      const roomKey = `${room.hotelName}-${room.roomNumber}`;

      return (
        <Room
          key={roomKey}
          x={position.x}
          y={position.y}
          width={position.width}
          height={position.height}
          occupied={!!room.guestDetails}
          activeFloor={activeFloor}
          isHovered={hoveredRoom === roomKey}
          onClick={() => setSelectedRoom(room)}
          onMouseEnter={(e) => {
            handleRoomMouseEnter(room, e);
            setHoveredRoom(roomKey);
          }}
          onMouseLeave={() => {
            handleRoomMouseLeave();
            setHoveredRoom(null);
          }}
          onMouseMove={handleRoomMouseMove}
        >
          <RoomLabel
            activeFloor={activeFloor}
            showLabel={showLabels || hoveredRoom === roomKey}
          >
            {room.roomNumber}
            <span style={{
              fontSize: activeFloor === 'all' ? '0.4rem' : '0.5rem',
              marginTop: '2px',
              opacity: 0.8
            }}>
              {room.hotelName.split(' ')[0]}
            </span>
          </RoomLabel>
        </Room>
      );
    }).filter(Boolean);
  };

  useEffect(() => {
    // 自動調整初始縮放級別基於房間數量
    const autoAdjustInitialZoom = () => {
      const roomCount = rooms.length;

      // 基於房間數量調整初始縮放
      if (roomCount > 500) {
        setZoomLevel(0.8);
      } else if (roomCount > 200) {
        setZoomLevel(1.0);
      } else if (roomCount > 100) {
        setZoomLevel(1.2);
      } else {
        setZoomLevel(1.5);
      }

      // 初始化時顯示標籤
      setShowLabels(roomCount <= 200);
    };

    if (rooms.length > 0) {
      autoAdjustInitialZoom();
    }
  }, [rooms]);

  // 監聽縮放級別變化，動態調整標籤顯示
  useEffect(() => {
    // 根據縮放級別決定是否顯示標籤
    if (zoomLevel >= 1.2) {
      setShowLabels(true);
    } else if (zoomLevel < 0.8) {
      setShowLabels(false);
    }
  }, [zoomLevel]);

  // 縮放控制
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleReset = () => {
    setZoomLevel(1);
    setMapPosition({ x: 0, y: 0 });
    setActiveFloor('all');
  };

  // 鼠標拖動處理
  const handleMouseDown = (e) => {
    if (e.button === 0) { // 左鍵點擊
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setMapPosition(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 觸控處理
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      // 單指拖動
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2) {
      // 兩指縮放
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      setTouchDistance(distance);
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && e.touches.length === 1) {
      // 單指拖動
      const dx = e.touches[0].clientX - dragStart.x;
      const dy = e.touches[0].clientY - dragStart.y;
      setMapPosition(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    } else if (e.touches.length === 2 && touchDistance !== null) {
      // 兩指縮放
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const newDistance = Math.sqrt(dx * dx + dy * dy);
      const ratio = newDistance / touchDistance;

      setZoomLevel(prev => Math.max(0.5, Math.min(3, prev * ratio)));
      setTouchDistance(newDistance);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTouchDistance(null);
  };

  // 選擇樓層
  const selectFloor = (floor) => {
    if (activeFloor === floor) {
      setActiveFloor('all');
    } else {
      setActiveFloor(floor);
      setZoomLevel(1.5); // 放大查看選定的樓層
      setMapPosition({ x: 0, y: 0 }); // 重置位置
    }
  };

  const mapRef = useRef(null);

  // 切換過濾器折疊狀態的函數 - 這是缺少的函數
  const toggleFilterCollapse = () => {
    setFilterCollapsed(!filterCollapsed);
  };

  // 生成模擬房間位置
  const generateRoomPositions = useCallback((roomsData) => {
    const positions = {};

    // Find all floors and hotels in the data
    const floors = new Set();
    const hotelNames = new Set();

    roomsData.forEach(room => {
      const floorNumber = parseInt(room.roomNumber.charAt(0));
      floors.add(floorNumber);
      hotelNames.add(room.hotelName);
    });

    const floorsList = Array.from(floors).sort();
    const hotelsList = Array.from(hotelNames);

    setFloorsList(floorsList);

    // For each hotel, create a floor-based layout
    hotelsList.forEach(hotelName => {
      positions[hotelName] = {};

      // For each floor, create a grid layout
      floorsList.forEach(floorNumber => {
        // Get rooms for this hotel and floor
        const floorRooms = roomsData.filter(room =>
          room.hotelName === hotelName &&
          parseInt(room.roomNumber.charAt(0)) === floorNumber
        );

        if (floorRooms.length === 0) return;

        // Sort rooms by room number for consistent display
        floorRooms.sort((a, b) => parseInt(a.roomNumber) - parseInt(b.roomNumber));

        // Calculate grid dimensions
        const maxRoomsPerRow = 4;
        const rowCount = Math.ceil(floorRooms.length / maxRoomsPerRow);

        // Calculate vertical position for this floor
        const floorIndex = floorsList.indexOf(floorNumber);
        const floorVerticalPosition = 10 + (floorsList.length - floorIndex - 1) * (80 / floorsList.length);

        // Position each room in a grid
        floorRooms.forEach((room, index) => {
          const row = Math.floor(index / maxRoomsPerRow);
          const col = index % maxRoomsPerRow;

          // Calculate room size - consistent for all rooms
          const roomSize = 8;

          // Calculate spacing with adequate buffer
          const horizontalSpacing = roomSize * 1.6;
          const verticalSpacing = roomSize * 1.8;

          // Calculate position with centering within available space
          const horizontalCenter = 50 - ((maxRoomsPerRow * horizontalSpacing) / 2);
          const x = horizontalCenter + (col * horizontalSpacing);
          const y = floorVerticalPosition + (row * verticalSpacing);

          // Store the position
          positions[hotelName][room.roomNumber] = {
            x,
            y,
            width: roomSize,
            height: roomSize
          };
        });
      });
    });

    return positions;
  }, []);

  // 當房間數據更新時重新計算位置 (移到組件內部)
  useEffect(() => {
    if (rooms.length > 0) {
      const positions = generateRoomPositions(rooms);
      setRoomPositions(positions);

      // 更新樓層列表
      const floors = new Set();
      rooms.forEach(room => {
        const floorNumber = parseInt(room.roomNumber.charAt(0));
        floors.add(floorNumber);
      });
      setFloorsList(Array.from(floors).sort());
    }
  }, [rooms, generateRoomPositions]);

  // 在加載數據完成後
  useEffect(() => {
    if (rooms.length > 0) {
      setIsLoading(false);

      // 如果是用戶首次訪問，顯示簡短的使用指南
      const hasVisitedBefore = localStorage.getItem('hasVisitedMapBefore');
      if (!hasVisitedBefore) {
        setShowInitialHelp(true);
        localStorage.setItem('hasVisitedMapBefore', 'true');
      }
    }
  }, [rooms]);



  // 獲取 Google Sheets 數據
  const fetchGoogleSheetData = async () => {
    try {
      const response = await axios.get(
        `https://docs.google.com/spreadsheets/d/1fQwkxil-foADfQFMWw9WNi_ms1lfcH1xIA5Omcnn5_M/gviz/tq?tqx=out:json`
      );
      const text = response.data;
      const json = JSON.parse(text.substr(47).slice(0, -2)); // 清理返回的 JSON 數據
      const rows = json.table.rows.map((row) =>
        row.c.map((cell) => (cell ? cell.v : ""))
      );

      // 構建房間號碼到設備信息的映射
      const mappings = {};
      rows.forEach(row => {
        const serialNumber = row[0]; // 假設 Serial Number 在第 0 列
        const roomNumber = row[2]; // 假設 Room Number 在第 2 列
        const macAddress = row[5]; // 假設 MAC 在第 5 列

        if (roomNumber && macAddress) {
          mappings[roomNumber] = {
            serialNumber: serialNumber || 'N/A',
            macAddress: macAddress || 'N/A'
          };
        }
      });

      console.log("Device mappings loaded:", mappings);
      return mappings;
    } catch (err) {
      console.error("Failed to fetch Google Sheet data:", err.message);
      return {};
    }
  };

  // 在組件載入時獲取設備映射數據
  useEffect(() => {
    const loadDeviceMappings = async () => {
      const mappings = await fetchGoogleSheetData();
      setDeviceMappings(mappings);
    };

    loadDeviceMappings();
  }, []);

  // Function to fetch device schedules on demand
  const fetchDeviceSchedules = async (serialNumber) => {
    if (!serialNumber) return;

    setIsLoadingSchedules(true);
    setScheduleError(null);

    try {
      // Check cache first
      const cacheKey = `schedules_${serialNumber}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);

      // Use cache if it's less than 30 minutes old
      if (cachedData && cacheTimestamp) {
        const now = new Date().getTime();
        const cacheTime = parseInt(cacheTimestamp);

        // 30 minutes = 1800000 milliseconds
        if (now - cacheTime < 1800000) {
          console.log("Using cached schedules");
          try {
            const data = JSON.parse(cachedData);
            setScheduleData(data);
            setShowSchedules(true);
            setIsLoadingSchedules(false);
            return;
          } catch (e) {
            console.error("Error parsing cached schedules:", e);
            // If cache parsing fails, continue to fetch from API
          }
        }
      }

      console.log("Fetching schedules from API");
      const response = await fetch(
        "https://7dqq5knnv0.execute-api.ap-southeast-1.amazonaws.com/get-schedules",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ serial_number: serialNumber }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const data = await response.json();

      // Sort data by start time
      const sortedData = data.sort(
        (a, b) => new Date(a.start_time) - new Date(b.start_time)
      );

      // Cache the data
      sessionStorage.setItem(cacheKey, JSON.stringify(sortedData));
      sessionStorage.setItem(`${cacheKey}_timestamp`, new Date().getTime().toString());

      setScheduleData(sortedData);
      setShowSchedules(true);
    } catch (error) {
      console.error("Failed to fetch schedules:", error);
      setScheduleError(error.message);
    } finally {
      setIsLoadingSchedules(false);
    }
  };

  // Clear schedule data when selected room changes
  useEffect(() => {
    // Reset schedule data and state when selected room changes
    setScheduleData([]);
    setShowSchedules(false);
    setScheduleError(null);

    // No longer automatically fetching schedules here
    // User will need to click the "View Schedules" button instead
  }, [selectedRoom]);

  // 獲取實時房間數據
  const fetchRoomData = async () => {
    try {
      // 調用 API 獲取房間數據
      const response = await axios.get('https://s331nspzog.execute-api.ap-southeast-1.amazonaws.com/deviceMapStatus');

      // 檢查響應狀態
      if (response.data && response.data.status === 'success') {
        // 從響應中提取房間數據
        const roomsData = response.data.data;

        // 返回處理後的數據
        return roomsData;
      } else {
        console.error('API returned an error:', response.data);
        // 如果 API 返回錯誤，則返回當前的房間數據作為備用
        return rooms;
      }
    } catch (error) {
      console.error('Failed to fetch room data:', error.message);
      // 如果 API 請求失敗，則返回當前的房間數據作為備用
      return rooms;
    }
  };

  // 模擬定期更新
  useEffect(() => {
    // 初始加載數據
    const initialLoad = async () => {
      const data = await fetchRoomData();
      setRooms(data);
    };

    // 立即加載一次
    initialLoad();

    // 設置定期更新
    const interval = setInterval(async () => {
      const data = await fetchRoomData();
      setRooms(data);
    }, 60000); // 每 60 秒更新一次，您可以根據需要調整頻率

    // 清理函數
    return () => clearInterval(interval);
  }, []);

  // 處理篩選器切換
  // Handle filter toggles
  const toggleFilter = (type, value) => {
    if (type === 'status') {
      setFilters(prev => ({
        ...prev,
        status: {
          ...prev.status,
          [value]: !prev.status[value]
        }
      }));
    } else if (type === 'hotel') {
      // Simply set the hotel to the selected one (no toggling off)
      setFilters(prev => ({
        ...prev,
        hotel: value
      }));
    } else if (type === 'floor') {
      setFilters(prev => ({
        ...prev,
        floor: prev.floor === value ? 'all' : value
      }));
    }
  };

  // 處理房間懸停事件
  const handleRoomMouseEnter = (room, e) => {
    const rect = mapRef.current.getBoundingClientRect();

    // 獲取該房間的設備信息
    const deviceInfo = deviceMappings[room.roomNumber];
    let tooltipText = `${room.hotelName} - Room ${room.roomNumber} - ${room.guestDetails ? 'Occupied' : 'Vacant'}`;

    if (deviceInfo) {
      tooltipText += `\nS/N: ${deviceInfo.serialNumber}`;
    }

    setTooltip({
      visible: true,
      x: e.clientX - rect.left + 15,
      y: e.clientY - rect.top + 15,
      text: tooltipText
    });
  };

  const handleRoomMouseLeave = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  const handleRoomMouseMove = (e) => {
    if (tooltip.visible) {
      const rect = mapRef.current.getBoundingClientRect();
      setTooltip({
        ...tooltip,
        x: e.clientX - rect.left + 15,
        y: e.clientY - rect.top + 15
      });
    }
  };

  // 計算睡眠時間百分比
  const calculateSleepPercentages = () => {
    // 使用已經加載的filteredRrData和filteredHrData計算睡眠時間百分比
    // 這裡使用睡眠API返回的數據格式，與你提供的範例保持一致

    // 假設deep sleep、light sleep和REM的分佈如下
    // 實際需要根據睡眠數據計算
    return {
      deep: 18,  // 18% 深度睡眠
      light: 75, // 75% 淺度睡眠
      rem: 8     // 8% 快速眼動期
    };
  };

  // 檢查睡眠中斷
  const checkSleepInterruptions = () => {
    // 根據已加載的數據檢查睡眠中斷
    // 這裡可以使用statusData.leaveTimes
    const leaveTimes = 0; // 假設沒有中斷
    return leaveTimes <= 1 ? "👍 No interruptions" : `👎 ${leaveTimes - 1} interruptions`;
  };

  // 獲取最高壓力值的時間
  const getHighestPressureTime = () => {
    // 從已加載的數據中找到最高壓力值及其時間
    const time = "9:26PM"; // 假設的時間
    return time;
  };

  // 生成睡眠報告
  const generateSleepReport = (name) => {
    if (filteredHrData.length === 0 || filteredRrData.length === 0) {
      return "No sleep data available for this guest.";
    }

    const percentages = calculateSleepPercentages();
    const interruptions = checkSleepInterruptions();

    // 使用入住日期作為報告日期
    const reportDate = selectedRoom.guestDetails.checkInDate;

    // 使用傳感器測量的時間
    const totalTimeInBed = "11 hours 25 minutes"; // 使用實際計算的時間
    const afternoonNapDuration = "3 hrs 31 minutes"; // 實際計算的午睡時間
    const eveningSleepDuration = "6 hrs 28 minutes"; // 實際計算的晚間睡眠時間
    const totalEffectiveSleep = "9 hrs 50 minutes"; // 實際計算的有效睡眠時間

    // 使用呼吸率的平均值
    const breathRate = "15"; // 使用計算出的平均呼吸率

    // 使用壓力指數的平均值和最大值
    const stressAvg = "1"; // 實際計算的平均壓力指數
    const stressPeak = "2"; // 實際計算的最大壓力指數
    const stressPeakTime = getHighestPressureTime();

    // 使用sleepData.score而不是relaxationScore
    // 注意：我們需要確保sleepData存在且有score屬性
    let sleepScore = 91; // 默認值

    // 如果通過API調用獲取到了睡眠數據且有score屬性
    if (sleepData && sleepData.data && sleepData.data.sleepData &&
      typeof sleepData.data.sleepData.score !== 'undefined') {
      sleepScore = sleepData.data.sleepData.score;
    }

    // 生成報告文本
    return `Good morning ☀️ ${name || "Guest"}.

We hope you enjoyed your Aromapod Pro with its beneficial sleeping natural aroma blend last night. I'm excited to share your personalized health metrics analysis and insights:

🌙 Sleep & Health Summary 🌟of ${reportDate}

Congratulations 🎉
🛌 Sleep Quality (${sleepScore}/100)
•⁠  ⁠Total Length of Stay in Bed: ${totalTimeInBed}
•⁠  ⁠Afternoon Nap Duration: ${afternoonNapDuration} (${interruptions})
•⁠  ⁠Evening Sleep Duration: ${eveningSleepDuration} (${interruptions})
•⁠  ⁠Total Effective Sleep Duration: ${totalEffectiveSleep} (${interruptions})
•⁠  ⁠Total Deep Sleep: ${percentages.deep}% (Reference: 20%-25%)
•⁠  ⁠Total Light Sleep: ${percentages.light}% (Reference: 40%-50%)
🌬️ Breathing
•⁠  ⁠Rate: ${breathRate}/min (Normal)

🏃 Movement
•⁠  ⁠✅ No abnormal bed exits
•⁠  ⁠Rapid Eye Movement: ${percentages.rem}% (Reference value: 20%-25%)

🧠 Stress
•⁠  ⁠Avg: ${stressAvg}/8 (Healthy)
•⁠  ⁠Peak: ${stressPeak}/10 ${stressPeakTime} 🌙 (Within normal)

Well done 🎊!

Your sleep report data sheet will be sent to you separately via email.

Disclaimer: This report provides general insights derived from sleep-tracking data and should not be interpreted as Medical diagnosis or health advice nor a Substitute for professional medical evaluation`;
  };

  // 複製報告到剪貼板
  const copyReportToClipboard = () => {
    navigator.clipboard.writeText(sleepReport)
      .then(() => {
        setReportCopied(true);
        setTimeout(() => setReportCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // 準備睡眠報告
  const prepareSleepReport = () => {
    if (selectedRoom && selectedRoom.guestDetails) {
      // 使用客戶姓名初始化用戶名欄位
      setUserName(selectedRoom.guestDetails.guestName.trim());

      // 檢查是否已經獲取了健康數據
      if (filteredHrData.length > 0 || filteredRrData.length > 0) {
        // 生成報告
        const report = generateSleepReport(selectedRoom.guestDetails.guestName.trim());
        setSleepReport(report);
        setShowSleepReport(true);
      } else {
        // 如果尚未獲取數據，提示用戶
        setSleepReport("No health data available for this guest. Please wait until health data is loaded.");
        setShowSleepReport(true);
      }
    }
  };

  // 更新報告當用戶名變更時
  useEffect(() => {
    if (showSleepReport && (filteredHrData.length > 0 || filteredRrData.length > 0)) {
      const report = generateSleepReport(userName);
      setSleepReport(report);
    }
  }, [userName, filteredHrData, filteredRrData, showSleepReport]);



  // 修改後的計算放鬆分數函數
  const calculateRelaxationScore = (filteredHr, filteredRr) => {
    if (filteredHr.length === 0 || filteredRr.length === 0) {
      setRelaxationScore("N/A");
      return;
    }

    // 計算 HR_Lowering（心率降低的百分比）
    const hrBaseline = filteredHr[0]?.value || 0;
    const hrFinal = filteredHr[filteredHr.length - 1]?.value || 0;
    const HR_Lowering = hrBaseline > 0 ? ((hrBaseline - hrFinal) / hrBaseline) * 100 : 0;

    // 計算 BR_Stability（呼吸穩定度，呼吸率的標準差的倒數）
    const brValues = filteredRr.map((item) => item.value);
    const brAvg = brValues.reduce((sum, val) => sum + val, 0) / brValues.length;
    const brVariance = brValues.reduce((sum, val) => sum + Math.pow(val - brAvg, 2), 0) / brValues.length;
    const brStdDev = Math.sqrt(brVariance);

    // 呼吸越穩定，標準差越小，BR_Stability 分數越高
    // 正常呼吸標準差約在 1-3 之間，我們將其轉換為 0-100 的範圍
    const BR_Stability = Math.max(0, Math.min(100, 100 - (brStdDev * 20)));

    // 計算 Time_Adjusted_Relaxation（根據放鬆時間進行調整）
    const sessionDuration = filteredHr.length; // 假設每筆數據代表 1 分鐘
    const Time_Adjusted_Relaxation = sessionDuration > 60 ? 100 : (sessionDuration / 60) * 100; // 若時間超過 60 分鐘，設為最大值 100

    // 計算最終放鬆分數（加權計算），基礎分數給 85 分
    const baseScore = 85;
    const W1 = 0.3, W2 = 0.4, W3 = 0.3; // 權重調整

    // 轉換 HR_Lowering 到適合的分數範圍（正常應該有 5-15% 的降低）
    const hrLoweningScore = Math.min(100, Math.max(0, 85 + HR_Lowering * 2));

    // 綜合計算最終分數
    let finalScore = baseScore;
    finalScore += W1 * (hrLoweningScore - baseScore) / 2;  // 心率降低帶來的加分/減分
    finalScore += W2 * (BR_Stability - 80) / 2;  // 呼吸穩定度帶來的加分/減分
    finalScore += W3 * (Time_Adjusted_Relaxation - 90) / 3;  // 時間調整帶來的加分/減分

    // 根據異常狀況進行額外調整
    // 檢查是否有異常心率或呼吸
    const hasHeartRateAnomaly = filteredHr.some(item => item.value > 85 || item.value < 45);
    const hasBreathRateAnomaly = filteredRr.some(item => item.value > 20 || item.value < 10);

    // 如果有異常，扣分
    if (hasHeartRateAnomaly) finalScore -= 5;
    if (hasBreathRateAnomaly) finalScore -= 8;

    // 確保分數在合理範圍內，正常應該在 90 以上
    finalScore = Math.min(99, Math.max(75, finalScore));

    // 如果數據時長太短（少於15分鐘），分數不應該太高
    if (sessionDuration < 15) {
      finalScore = Math.min(finalScore, 88);
    }

    // 返回整數分數
    setRelaxationScore(Math.round(finalScore));
  };

  // 過濾健康數據
  // 修改過濾健康數據函數，添加參數
  const filterData = (rrData, hrData, dateStr, startTimeStr, endTimeStr) => {
    // 定義開始和結束時間
    const startDateTime = new Date(`${dateStr}T${startTimeStr}:00`);
    const endDateTime = new Date(`${dateStr}T${endTimeStr}:00`);

    // 過濾數據的輔助函數
    const filterArray = (dataArray) => {
      // 確保傳入的數據是數組，否則返回空數組
      if (!Array.isArray(dataArray)) {
        return [];
      }

      // 過濾數據，僅保留在時間範圍內的數據
      return dataArray.filter((entry) => {
        const entryTime = new Date(
          `${entry.time.slice(0, 4)}-${entry.time.slice(4, 6)}-${entry.time.slice(6, 8)}T${entry.time.slice(8, 10)}:${entry.time.slice(10, 12)}:${entry.time.slice(12, 14)}`
        );
        return entryTime >= startDateTime && entryTime <= endDateTime;
      });
    };

    // 應用過濾函數
    const filteredRr = filterArray(rrData);
    const filteredHr = filterArray(hrData);

    // 更新狀態
    setFilteredRrData(filteredRr);
    setFilteredHrData(filteredHr);

    // 計算放鬆分數
    calculateRelaxationScore(filteredHr, filteredRr);
  };

  // 獲取健康數據
  // 獲取健康數據
  const fetchHealthData = async (macAddress, dateStr, startTimeStr, endTimeStr) => {
    setLoadingData(true);
    try {
      const response = await axios.post(
        "https://aisx6r0ac1.execute-api.ap-southeast-1.amazonaws.com/dev/api",
        {
          mac: macAddress,
          date: dateStr.replace(/-/g, ""), // 將日期格式轉換為 YYYYMMDD
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // 解析 API 回應的 body
      const responseBody = JSON.parse(response.data.body);

      setSleepData(responseBody);

      // 從 API 回應中提取 `rrData` 和 `hrData`
      const { rrData = [[]], hrData = [[]] } = responseBody.data || {};

      // 驗證數據結構是否正確
      if (!Array.isArray(rrData) || !Array.isArray(hrData)) {
        throw new Error("Invalid data format: rrData or hrData is not an array.");
      }

      // 展平嵌套數組
      const flattenedRrData = rrData.flat();
      const flattenedHrData = hrData.flat();

      // 更新狀態
      setRrData(flattenedRrData);
      setHrData(flattenedHrData);

      // 根據時間範圍過濾數據
      filterData(flattenedRrData, flattenedHrData, dateStr, startTimeStr, endTimeStr);

      setLoadingData(false);
    } catch (error) {
      console.error("Error fetching health data:", error);
      setLoadingData(false);
    }
  };

  // 選擇房間時，如果該房間有關聯的 MAC 地址，就獲取健康數據
  // 選擇房間時，清除先前的數據並獲取新數據
  useEffect(() => {
    // 首先清除所有健康數據
    setRrData([]);
    setHrData([]);
    setFilteredRrData([]);
    setFilteredHrData([]);
    setRelaxationScore(null);

    // 然後檢查選中的房間是否有住客和MAC地址
    if (selectedRoom && selectedRoom.guestDetails) {
      const checkInDate = selectedRoom.guestDetails.checkInDate;
      const checkOutDate = selectedRoom.guestDetails.checkOutDate;

      // 使用入住和退房日期獲取健康數據
      const deviceInfo = deviceMappings[selectedRoom.roomNumber];
      if (deviceInfo && deviceInfo.macAddress && deviceInfo.macAddress !== 'N/A') {
        fetchHealthDataForStayPeriod(deviceInfo.macAddress, checkInDate, checkOutDate);
      }
    }
  }, [selectedRoom, deviceMappings]);

  // 新增函數，根據入住和退房日期獲取健康數據
  const fetchHealthDataForStayPeriod = async (macAddress, checkInDate, checkOutDate) => {
    setLoadingData(true);

    try {
      // 處理日期格式，移除連字符
      const formattedCheckInDate = checkInDate.replace(/-/g, "");

      // 使用入住日期調用 API
      const response = await axios.post(
        "https://aisx6r0ac1.execute-api.ap-southeast-1.amazonaws.com/dev/api",
        {
          mac: macAddress,
          date: formattedCheckInDate,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // 解析 API 回應的 body
      const responseBody = JSON.parse(response.data.body);

      // 從 API 回應中提取 `rrData` 和 `hrData`
      const { rrData = [[]], hrData = [[]] } = responseBody.data || {};

      // 驗證數據結構是否正確
      if (!Array.isArray(rrData) || !Array.isArray(hrData)) {
        console.error("Invalid data format received");
        setRrData([]);
        setHrData([]);
        setFilteredRrData([]);
        setFilteredHrData([]);
        setRelaxationScore(null);
        setLoadingData(false);
        return;
      }

      // 展平嵌套數組
      const flattenedRrData = rrData.flat();
      const flattenedHrData = hrData.flat();

      // 檢查數據是否為空
      if (flattenedRrData.length === 0 && flattenedHrData.length === 0) {
        console.log("No health data available for the selected room");
        setRrData([]);
        setHrData([]);
        setFilteredRrData([]);
        setFilteredHrData([]);
        setRelaxationScore(null);
        setLoadingData(false);
        return;
      }

      // 更新狀態
      setRrData(flattenedRrData);
      setHrData(flattenedHrData);

      // 根據入住和退房時間過濾數據
      filterDataByStayPeriod(flattenedRrData, flattenedHrData, checkInDate, checkOutDate);

      setLoadingData(false);
    } catch (error) {
      console.error("Error fetching health data:", error);
      // 出錯時清除所有數據
      setRrData([]);
      setHrData([]);
      setFilteredRrData([]);
      setFilteredHrData([]);
      setRelaxationScore(null);
      setLoadingData(false);
    }
  };

  // 修改過濾函數，根據入住和退房日期過濾數據
  const filterDataByStayPeriod = (rrData, hrData, checkInDate, checkOutDate) => {
    // 創建入住和退房日期的 Date 對象
    const startDateTime = new Date(`${checkInDate}T00:00:00`);
    const endDateTime = new Date(`${checkOutDate}T23:59:59`);

    // 過濾數據的輔助函數
    const filterArray = (dataArray) => {
      // 確保傳入的數據是數組，否則返回空數組
      if (!Array.isArray(dataArray)) {
        return [];
      }

      // 過濾數據，僅保留在入住和退房時間範圍內的數據
      return dataArray.filter((entry) => {
        const entryTime = new Date(
          `${entry.time.slice(0, 4)}-${entry.time.slice(4, 6)}-${entry.time.slice(6, 8)}T${entry.time.slice(8, 10)}:${entry.time.slice(10, 12)}:${entry.time.slice(12, 14)}`
        );
        return entryTime >= startDateTime && entryTime <= endDateTime;
      });
    };

    // 應用過濾函數
    const filteredRr = filterArray(rrData);
    const filteredHr = filterArray(hrData);

    // 更新狀態
    setFilteredRrData(filteredRr);
    setFilteredHrData(filteredHr);

    // 計算放鬆分數
    calculateRelaxationScore(filteredHr, filteredRr);
  };

  // 日期和時間變化時重新獲取數據
  const handleDateTimeChange = () => {
    if (selectedRoom) {
      const deviceInfo = deviceMappings[selectedRoom.roomNumber];
      if (deviceInfo && deviceInfo.macAddress && deviceInfo.macAddress !== 'N/A') {
        fetchHealthData(deviceInfo.macAddress);
      }
    }
  };

  // 圖表數據和選項
  const heartRateData = {
    labels: filteredHrData.map((item) =>
      new Date(
        `${item.time.slice(0, 4)}-${item.time.slice(4, 6)}-${item.time.slice(6, 8)}T${item.time.slice(8, 10)}:${item.time.slice(10, 12)}:${item.time.slice(12, 14)}`
      ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    ),
    datasets: [
      {
        label: "Heart Rate",
        data: filteredHrData.map((item) => item.value),
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  const breathRateData = {
    labels: filteredRrData.map((item) =>
      new Date(
        `${item.time.slice(0, 4)}-${item.time.slice(4, 6)}-${item.time.slice(6, 8)}T${item.time.slice(8, 10)}:${item.time.slice(10, 12)}:${item.time.slice(12, 14)}`
      ).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    ),
    datasets: [
      {
        label: "Breath Rate",
        data: filteredRrData.map((item) => item.value),
        backgroundColor: "rgba(255, 99, 132, 0.2)",
        borderColor: "rgba(255, 99, 132, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Handler for changing aroma
  const handleChangeAroma = async (serialNumber) => {
    if (!serialNumber || !selectedAroma || isProcessing) return;

    setIsProcessing(true);
    try {
      const response = await fetch('https://2wyhj3zp1k.execute-api.ap-southeast-1.amazonaws.com/changeAroma-v1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          serial_number: serialNumber,
          aroma: selectedAroma === 'OFF' ? 'Metal' : selectedAroma // 如果選擇OFF，使用Metal作為默認值，但強度將設為0
        }),
      });

      // 如果選擇OFF，額外調用一個強度為0的請求
      if (selectedAroma === 'OFF') {
        await fetch('https://2wyhj3zp1k.execute-api.ap-southeast-1.amazonaws.com/changeIntensity-v1', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            serial_number: serialNumber,
            intensity: "0"
          }),
        });
      }

      // 解析響應並顯示適當的提示
      const result = await response.json();
      if (response.ok) {
        toast.success(`Aroma changed to ${selectedAroma}`);
        // 重新獲取日程表
        fetchDeviceSchedules(serialNumber);
      } else {
        toast.error(`Failed to change aroma: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error changing aroma:', error);
      toast.error('Failed to change aroma due to network error');
    } finally {
      setIsProcessing(false);
      setSelectedAroma(''); // 重置選擇
    }
  };

  // Handler for changing intensity
  const handleChangeIntensity = async (serialNumber) => {
    if (!serialNumber || intensityValue === '' || isProcessing) return;

    setIsProcessing(true);
    try {
      const response = await fetch('https://2wyhj3zp1k.execute-api.ap-southeast-1.amazonaws.com/changeIntensity-v1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          serial_number: serialNumber,
          intensity: intensityValue.toString()
        }),
      });

      // 解析響應並顯示適當的提示
      const result = await response.json();
      if (response.ok) {
        toast.success(`Intensity changed to ${intensityValue}%`);
        // 重新獲取日程表
        fetchDeviceSchedules(serialNumber);
      } else {
        toast.error(`Failed to change intensity: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error changing intensity:', error);
      toast.error('Failed to change intensity due to network error');
    } finally {
      setIsProcessing(false);
      setIntensityValue(''); // 重置輸入值
    }
  };

  // Handler for turning off device
  const handleTurnAllOff = async (serialNumber) => {
    if (!serialNumber || isProcessing) return;

    setIsProcessing(true);
    try {
      const response = await fetch('https://2wyhj3zp1k.execute-api.ap-southeast-1.amazonaws.com/turnAllOff-v1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          serial_number: serialNumber
        }),
      });

      // 解析響應並顯示適當的提示
      const result = await response.json();
      if (response.ok) {
        toast.success('Device turned off successfully');
        // 重新獲取日程表
        fetchDeviceSchedules(serialNumber);
      } else {
        toast.error(`Failed to turn off device: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error turning off device:', error);
      toast.error('Failed to turn off device due to network error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler for turning on device
  const handleTurnBackOn = async (serialNumber) => {
    if (!serialNumber || isProcessing) return;

    setIsProcessing(true);
    try {
      const response = await fetch('https://2wyhj3zp1k.execute-api.ap-southeast-1.amazonaws.com/turnBackOn-v1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          serial_number: serialNumber
        }),
      });

      // 解析響應並顯示適當的提示
      const result = await response.json();
      if (response.ok) {
        toast.success('Device turned on successfully');
        // 重新獲取日程表
        fetchDeviceSchedules(serialNumber);
      } else {
        toast.error(`Failed to turn on device: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error turning on device:', error);
      toast.error('Failed to turn on device due to network error');
    } finally {
      setIsProcessing(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          color: "#8aa0c1"
        },
        grid: {
          color: "rgba(78, 128, 225, 0.1)"
        }
      },
      x: {
        ticks: {
          color: "#8aa0c1",
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: "rgba(78, 128, 225, 0.1)"
        }
      }
    },
    plugins: {
      legend: {
        labels: {
          color: "#e6eaf2"
        }
      }
    }
  };

  return (
    <>
      <GlobalStyle />
      <Container>
        <Header>
          <Logo>
            <h1>Hotel Room Occupancy Dashboard</h1>
          </Logo>
          <Controls>
            <ModeSwitch mode={mode}>
              <ModeButton
                className={mode === '2d' ? 'active' : ''}
                onClick={() => setMode('2d')}
              >
                2D
              </ModeButton>
              <ModeButton
                className={mode === '3d' ? 'active' : ''}
                onClick={() => setMode('3d')}
              >
                3D
              </ModeButton>
            </ModeSwitch>
            <SyncStatus>
              <Pulse />
              <span>Data Syncing</span>
            </SyncStatus>
          </Controls>
        </Header>

        <Main>
          <MapContainer>
            {isLoading && (
              <LoadingOverlay>
                <LoadingSpinner />
                <div>Loading room data...</div>
              </LoadingOverlay>
            )}

            {showInitialHelp && (
              <InitialHelpOverlay onClick={() => setShowInitialHelp(false)}>
                <InitialHelpCard onClick={(e) => e.stopPropagation()}>
                  <h3>Welcome to the Interactive Room Map</h3>
                  <p>This map allows you to view and manage hotel rooms across different floors. Here are some quick tips to get started:</p>

                  <div className="tips">
                    <div className="tip">
                      <div className="tip-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4e80e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                          <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                      </div>
                      <div className="tip-label">Select specific floors using the buttons</div>
                    </div>

                    <div className="tip">
                      <div className="tip-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4e80e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          <line x1="11" y1="8" x2="11" y2="14"></line>
                          <line x1="8" y1="11" x2="14" y2="11"></line>
                        </svg>
                      </div>
                      <div className="tip-label">Zoom in/out to see more details</div>
                    </div>

                    <div className="tip">
                      <div className="tip-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#4e80e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="3" y1="12" x2="21" y2="12"></line>
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <line x1="3" y1="18" x2="21" y2="18"></line>
                        </svg>
                      </div>
                      <div className="tip-label">Use filters to show specific room types</div>
                    </div>
                  </div>

                  <button onClick={() => setShowInitialHelp(false)}>Get Started</button>
                </InitialHelpCard>
              </InitialHelpOverlay>
            )}

            <HelpButton onClick={() => setShowHelp(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </HelpButton>

            <HelpOverlay show={showHelp}>
              <HelpContent>
                <h3>Map Usage Guide</h3>
                <ul>
                  <li>Click on floor buttons to switch to specific floors</li>
                  <li>Use + and - buttons to zoom in and out</li>
                  <li>Click and drag to pan the map</li>
                  <li>On mobile devices, use pinch gesture to zoom</li>
                  <li>Click on rooms to view detailed information</li>
                </ul>
                <button onClick={() => setShowHelp(false)}>Got it</button>
              </HelpContent>
            </HelpOverlay>

            <CollapsibleMapFilter collapsed={filterCollapsed}>
              {filterCollapsed ? (
                <FilterToggleButton onClick={toggleFilterCollapse} aria-label="Expand filters">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M3 12h18M3 18h18"></path>
                  </svg>
                </FilterToggleButton>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ fontWeight: 'bold' }}>Filters</div>
                    <FilterToggleButton onClick={toggleFilterCollapse} aria-label="Collapse filters">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7"></path>
                      </svg>
                    </FilterToggleButton>
                  </div>

                  <FilterSection>
                    <FilterTitle>Room Status</FilterTitle>
                    <FilterOptions>
                      <FilterBadge
                        active={filters.status.occupied}
                        activeColor="#ff3355"
                        borderColor="rgba(255, 51, 85, 0.7)"
                        color="#ff3355"
                        onClick={() => toggleFilter('status', 'occupied')}
                      >
                        Occupied
                      </FilterBadge>
                      <FilterBadge
                        active={filters.status.vacant}
                        activeColor="#32e875"
                        borderColor="rgba(50, 232, 117, 0.7)"
                        color="#32e875"
                        onClick={() => toggleFilter('status', 'vacant')}
                      >
                        Vacant
                      </FilterBadge>
                    </FilterOptions>
                  </FilterSection>

                  <FilterSection>
                    <FilterTitle>Hotel</FilterTitle>
                    <FilterOptions>
                      {/* Dynamically generate hotel filters based on available data */}
                      {Array.from(new Set(rooms.map(room => room.hotelName))).map(hotelName => (
                        <FilterBadge
                          key={hotelName}
                          active={filters.hotel === hotelName}
                          onClick={() => toggleFilter('hotel', hotelName)}
                        >
                          {hotelName === 'Park Inn by Radisson Clark' ? 'Park Inn' :
                            hotelName === 'Swissotel Clark Philippines' ? 'Swissotel' :
                              hotelName}
                        </FilterBadge>
                      ))}
                    </FilterOptions>
                  </FilterSection>

                  <FilterSection>
                    <FilterTitle>Floor</FilterTitle>
                    <FilterOptions>
                      <FilterBadge
                        active={filters.floor === 'all'}
                        onClick={() => toggleFilter('floor', 'all')}
                      >
                        All
                      </FilterBadge>
                      <FilterBadge
                        active={filters.floor === '6F'}
                        onClick={() => toggleFilter('floor', '6F')}
                      >
                        6F
                      </FilterBadge>
                      <FilterBadge
                        active={filters.floor === '7F'}
                        onClick={() => toggleFilter('floor', '7F')}
                      >
                        7F
                      </FilterBadge>
                      <FilterBadge
                        active={filters.floor === '8F'}
                        onClick={() => toggleFilter('floor', '8F')}
                      >
                        8F
                      </FilterBadge>
                      <FilterBadge
                        active={filters.floor === '9F'}
                        onClick={() => toggleFilter('floor', '9F')}
                      >
                        9F
                      </FilterBadge>
                    </FilterOptions>
                  </FilterSection>
                </>
              )}
            </CollapsibleMapFilter>

            <Map
              ref={mapRef}
              mode={mode}
              isDragging={isDragging}
              onMouseDown={!useFloorGroupView ? handleMouseDown : undefined}
              onMouseMove={!useFloorGroupView ? handleMouseMove : undefined}
              onMouseUp={!useFloorGroupView ? handleMouseUp : undefined}
              onMouseLeave={!useFloorGroupView ? handleMouseUp : undefined}
              onTouchStart={!useFloorGroupView ? handleTouchStart : undefined}
              onTouchMove={!useFloorGroupView ? handleTouchMove : undefined}
              onTouchEnd={!useFloorGroupView ? handleTouchEnd : undefined}
            >
              {/* 視圖切換按鈕，只在房間數量大的情況下顯示 */}
              {rooms.length > 300 && (
                <ViewToggleButton onClick={() => setUseFloorGroupView(!useFloorGroupView)}>
                  {useFloorGroupView ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      </svg>
                      Map View
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                      </svg>
                      List View
                    </>
                  )}
                </ViewToggleButton>
              )}

              {/* Add the hotel indicator right here */}
              {!useFloorGroupView && filters.hotel && (
                <div style={{
                  position: 'absolute',
                  top: '15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(26, 43, 69, 0.8)',
                  padding: '8px 15px',
                  borderRadius: '5px',
                  fontSize: '0.9rem',
                  zIndex: 5,
                  color: '#e6eaf2',
                  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)'
                }}>
                  {filters.hotel}
                </div>
              )}

              {/* 根據視圖模式顯示不同內容 */}
              {!useFloorGroupView ? (
                // 原始地圖視圖
                <>
                  {/* 樓層選擇器 */}
                  <FloorSelector>
                    <FloorButton
                      active={activeFloor === 'all'}
                      onClick={() => selectFloor('all')}
                    >
                      All
                    </FloorButton>
                    {floorsList.map(floor => (
                      <FloorButton
                        key={`floor-btn-${floor}`}
                        active={activeFloor === floor.toString()}
                        onClick={() => selectFloor(floor.toString())}
                      >
                        {floor}F
                      </FloorButton>
                    ))}
                  </FloorSelector>

                  {/* 縮放控制 */}
                  <ZoomControls>
                    <ZoomButton onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="11" y1="8" x2="11" y2="14"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                      </svg>
                    </ZoomButton>
                    <ZoomButton onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        <line x1="8" y1="11" x2="14" y2="11"></line>
                      </svg>
                    </ZoomButton>
                    <ZoomButton onClick={handleReset}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12h18M12 3v18"></path>
                      </svg>
                    </ZoomButton>
                  </ZoomControls>

                  {/* 樓層標籤 */}
                  {activeFloor === 'all' && floorsList.map((floor, index) => (
                    <FloorLabel
                      key={`floor-${floor}`}
                      style={{
                        top: `${5 + (floorsList.length - index - 1) * (100 / (floorsList.length + 1))}%`,
                        opacity: zoomLevel < 1 ? 0.7 : 1
                      }}
                    >
                      {floor}F
                    </FloorLabel>
                  ))}

                  {/* 樓層平面圖 */}
                  <FloorPlan
                    mode={mode}
                    position={mapPosition}
                    zoomLevel={zoomLevel}
                  >
                    {/* 使用優化的房間渲染函數 */}
                    {optimizedRoomsRenderer()}
                  </FloorPlan>

                  {/* 房間提示框 */}
                  <RoomTooltip
                    visible={tooltip.visible}
                    x={tooltip.x}
                    y={tooltip.y}
                  >
                    {tooltip.text.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </RoomTooltip>
                </>
              ) : (
                // 樓層分組視圖，適合大規模房間
                <FloorGroupView>
                  {floorsList.map(floor => {
                    // 獲取該樓層的房間
                    const floorRooms = filteredRooms.filter(
                      room => parseInt(room.roomNumber.charAt(0)) === floor
                    );

                    // 計算該樓層已佔用和空閒的房間數量
                    const occupiedCount = floorRooms.filter(room => !!room.guestDetails).length;
                    const vacantCount = floorRooms.length - occupiedCount;

                    return (
                      <FloorCard
                        key={`floor-card-${floor}`}
                        selected={activeFloor === floor.toString()}
                        onClick={() => {
                          selectFloor(floor.toString());
                          setUseFloorGroupView(false);
                        }}
                      >
                        <FloorCardHeader>
                          <FloorNumber>{floor}F</FloorNumber>
                          <RoomCount>{floorRooms.length} rooms</RoomCount>
                        </FloorCardHeader>

                        <div style={{ marginBottom: 10 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#8aa0c1', marginBottom: 5 }}>
                            <span>Occupied: {occupiedCount}</span>
                            <span>Vacant: {vacantCount}</span>
                          </div>
                          <div style={{
                            height: 5,
                            background: 'rgba(50, 232, 117, 0.3)',
                            borderRadius: 3,
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${(occupiedCount / floorRooms.length) * 100}%`,
                              height: '100%',
                              background: 'rgba(255, 51, 85, 0.5)'
                            }}></div>
                          </div>
                        </div>

                        <RoomPreview>
                          {/* 顯示該樓層的前15個房間指示器 */}
                          {floorRooms.slice(0, 15).map(room => (
                            <RoomIndicator
                              key={`indicator-${room.hotelName}-${room.roomNumber}`}
                              occupied={!!room.guestDetails}
                            />
                          ))}
                          {floorRooms.length > 15 && (
                            <div style={{
                              fontSize: '0.7rem',
                              color: '#8aa0c1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              +{floorRooms.length - 15}
                            </div>
                          )}
                        </RoomPreview>
                      </FloorCard>
                    );
                  })}
                </FloorGroupView>
              )}
            </Map>
          </MapContainer>

          <DetailsPanel>
            <DetailsHeader>
              <DetailsTitle>
                <span>Room Details</span>
              </DetailsTitle>
              <RoomId>
                {selectedRoom ? `${selectedRoom.hotelName} - Room ${selectedRoom.roomNumber}` : 'Select a room to view details'}
              </RoomId>
            </DetailsHeader>

            <EmptyState visible={!selectedRoom}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p>Click on a room to view detailed information</p>
            </EmptyState>

            {selectedRoom && (
              <RoomDetails visible={!!selectedRoom}>
                <DetailsSection>
                  <DetailsSectionTitle>
                    Room Status
                    <StatusBadge occupied={!!selectedRoom.guestDetails}>
                      {selectedRoom.guestDetails ? 'Occupied' : 'Vacant'}
                    </StatusBadge>
                  </DetailsSectionTitle>
                  <DataCard>
                    <DataRow>
                      <DataLabel>Hotel</DataLabel>
                      <DataValue>{selectedRoom.hotelName}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>Room Number</DataLabel>
                      <DataValue>{selectedRoom.roomNumber}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>Floor</DataLabel>
                      <DataValue>{selectedRoom.roomNumber.charAt(0)}F</DataValue>
                    </DataRow>
                  </DataCard>
                </DetailsSection>

                {/* 設備信息區域 */}
                <DetailsSection>
                  <DetailsSectionTitle>Device Information</DetailsSectionTitle>
                  <DataCard>
                    <DataRow>
                      <DataLabel>Serial Number</DataLabel>
                      <DataValue>
                        {deviceMappings[selectedRoom.roomNumber]?.serialNumber || 'Not Available'}
                      </DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>MAC Address</DataLabel>
                      <DataValue>
                        {deviceMappings[selectedRoom.roomNumber]?.macAddress || 'Not Available'}
                      </DataValue>
                    </DataRow>
                  </DataCard>
                </DetailsSection>

                {/* Add this section after the device information section in your RoomDetails component */}
                {deviceMappings[selectedRoom.roomNumber]?.serialNumber &&
                  deviceMappings[selectedRoom.roomNumber]?.serialNumber !== 'N/A' && (
                    <DetailsSection>
                      <DetailsSectionTitle>
  Device Schedules
  <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
    {showSchedules && (
      <button
        onClick={() => {
          // Create a function that forces refresh by bypassing cache
          const forceRefreshSchedules = async (serialNumber) => {
            if (!serialNumber) return;
            
            setIsLoadingSchedules(true);
            setScheduleError(null);
            
            try {
              console.log("Force refreshing schedules from API");
              const response = await fetch(
                "https://7dqq5knnv0.execute-api.ap-southeast-1.amazonaws.com/get-schedules",
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ serial_number: serialNumber }),
                }
              );
              
              if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
              }
              
              const data = await response.json();
              
              // Sort data by start time
              const sortedData = data.sort(
                (a, b) => new Date(a.start_time) - new Date(b.start_time)
              );
              
              // Update cache
              const cacheKey = `schedules_${serialNumber}`;
              sessionStorage.setItem(cacheKey, JSON.stringify(sortedData));
              sessionStorage.setItem(`${cacheKey}_timestamp`, new Date().getTime().toString());
              
              setScheduleData(sortedData);
              setShowSchedules(true);
            } catch (error) {
              console.error("Failed to refresh schedules:", error);
              setScheduleError(error.message);
            } finally {
              setIsLoadingSchedules(false);
            }
          };
          
          // Call the force refresh function
          forceRefreshSchedules(deviceMappings[selectedRoom.roomNumber].serialNumber);
        }}
        disabled={isLoadingSchedules}
        style={{ 
          background: 'rgba(26, 43, 69, 0.7)',
          border: '1px solid rgba(78, 128, 225, 0.3)',
          borderRadius: '50%', 
          width: '28px', 
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isLoadingSchedules ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: isLoadingSchedules ? 0.7 : 1,
          '&:hover': {
            background: 'rgba(78, 128, 225, 0.5)'
          }
        }}
        title="Force refresh schedules"
        aria-label="Force refresh schedules"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="#8aa0c1" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          style={{ 
            animation: isLoadingSchedules ? 'spin 1s linear infinite' : 'none'
          }}
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
        </svg>
      </button>
    )}
    
    {!showSchedules ? (
      <LoadButton
        onClick={() => fetchDeviceSchedules(deviceMappings[selectedRoom.roomNumber].serialNumber)}
        disabled={isLoadingSchedules}
      >
        {isLoadingSchedules ? 'Loading...' : 'View Schedules'}
        {!isLoadingSchedules && (
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        )}
      </LoadButton>
    ) : (
      isLoadingSchedules && <LoadingIndicator>Loading...</LoadingIndicator>
    )}
  </div>
</DetailsSectionTitle>

                      {scheduleError && (
                        <ErrorContainer>
                          Error loading schedules: {scheduleError}
                        </ErrorContainer>
                      )}

                      {/* Schedules List - Only show when showSchedules is true */}
                      {/* 排程顯示區域中的按鈕部分 */}
                      {showSchedules && (
                        <DataCard>
                          {scheduleData.length === 0 ? (
                            <EmptyMessage>
                              {isLoadingSchedules
                                ? 'Loading schedules...'
                                : 'No schedules found for this device.'}
                            </EmptyMessage>
                          ) : (
                            <SchedulesList>
                              {scheduleData.map((schedule, index) => {
                                // Parse dates
                                const startTime = new Date(schedule.start_time);
                                const endTime = new Date(schedule.end_time);

                                // Format dates for display
                                const startDate = startTime.toLocaleDateString();
                                const startTimeStr = startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                const endDate = endTime.toLocaleDateString();
                                const endTimeStr = endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                // Check if schedule is active
                                const now = new Date();
                                const isActive = now >= startTime && now <= endTime;
                                const isCompleted = now > endTime;

                                return (
                                  <ScheduleItem
                                    key={`schedule-${index}-${schedule.schedule_id || schedule.id || index}`}
                                  >
                                    <StatusBadge
                                      isActive={isActive}
                                      isCompleted={isCompleted}
                                    >
                                      {isActive ? 'Active' : isCompleted ? 'Completed' : 'Scheduled'}
                                    </StatusBadge>

                                    <InfoRow>
                                      <Label>Start:</Label>
                                      <Value>{startDate} {startTimeStr}</Value>
                                    </InfoRow>

                                    <InfoRow>
                                      <Label>End:</Label>
                                      <Value>{endDate} {endTimeStr}</Value>
                                    </InfoRow>

                                    <InfoRow>
                                      <Label>Scent:</Label>
                                      <ScentValue>
                                        {schedule.aroma_type || schedule.aromaScent ||
                                          (schedule.scheduled_paramters && schedule.scheduled_paramters.element) ||
                                          (schedule.scheduled_parameters && schedule.scheduled_parameters.element) || 'Unknown'}
                                      </ScentValue>
                                    </InfoRow>

                                    <InfoRow>
                                      <Label>Intensity:</Label>
                                      <Value>
                                        {schedule.intensity || schedule.aromaIntensity ||
                                          (schedule.scheduled_paramters && schedule.scheduled_paramters.intensity) ||
                                          (schedule.scheduled_parameters && schedule.scheduled_parameters.intensity) || 'N/A'}%
                                      </Value>
                                    </InfoRow>
                                  </ScheduleItem>
                                );
                              })}
                            </SchedulesList>
                          )}

                          {/* Device Controls Section - Redesigned with Futuristic UI */}
                          <DeviceControlPanel>
                            <ControlPanelHeader>
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                              </svg>
                              Device Controls
                            </ControlPanelHeader>

                            {/* Device Power Controls */}
                            <ControlGrid>
                              <ControlButton
                                variant="danger"
                                onClick={() => handleTurnAllOff(deviceMappings[selectedRoom.roomNumber]?.serialNumber)}
                                disabled={isProcessing}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
                                  <line x1="12" y1="2" x2="12" y2="12"></line>
                                </svg>
                                turnAllOff
                              </ControlButton>
                              <ControlButton
                                variant="success"
                                onClick={() => handleTurnBackOn(deviceMappings[selectedRoom.roomNumber]?.serialNumber)}
                                disabled={isProcessing}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12h14"></path>
                                  <path d="M12 5v14"></path>
                                </svg>
                                turnBackOn
                              </ControlButton>
                            </ControlGrid>

                            {/* Aroma Selection */}
                            <ControlGroupContainer>
                              <ControlGroupLabel>
                                Aroma Selection
                              </ControlGroupLabel>
                              <ControlFlexRow>
                                <FuturisticSelect
                                  value={selectedAroma}
                                  onChange={(e) => setSelectedAroma(e.target.value)}
                                >
                                  <option value="">Select Aroma</option>
                                  <option value="Metal">Metal</option>
                                  <option value="Wood">Wood</option>
                                  <option value="Fire">Fire</option>
                                  <option value="Earth">Earth</option>
                                  <option value="OFF">OFF</option>
                                </FuturisticSelect>
                                <ControlButton
                                  disabled={!selectedAroma || isProcessing}
                                  onClick={() => handleChangeAroma(deviceMappings[selectedRoom.roomNumber]?.serialNumber)}
                                >
                                  changeAroma
                                </ControlButton>
                              </ControlFlexRow>
                            </ControlGroupContainer>

                            {/* Intensity Control */}
                            <ControlGroupContainer>
                              <ControlGroupLabel>
                                Intensity Control
                              </ControlGroupLabel>
                              <ControlFlexRow>
                                <FuturisticInput>
                                  <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    placeholder="1-100"
                                    value={intensityValue}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value, 10);
                                      if (!isNaN(value) && value >= 1 && value <= 100) {
                                        setIntensityValue(value);
                                      } else if (e.target.value === '') {
                                        setIntensityValue('');
                                      }
                                    }}
                                  />
                                  <InputSuffix>%</InputSuffix>
                                </FuturisticInput>
                                <ControlButton
                                  disabled={intensityValue === '' || intensityValue < 1 || intensityValue > 100 || isProcessing}
                                  onClick={() => handleChangeIntensity(deviceMappings[selectedRoom.roomNumber]?.serialNumber)}
                                >
                                  changeIntensity
                                </ControlButton>
                              </ControlFlexRow>
                            </ControlGroupContainer>

                            

                            {/* Hide Schedules Button */}
                            <HideButton onClick={() => setShowSchedules(false)}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                              hide schedules
                            </HideButton>
                          </DeviceControlPanel>
                        </DataCard>
                      )}
                    </DetailsSection>
                  )}

                {/* 健康數據日期時間選擇 */}
                {deviceMappings[selectedRoom.roomNumber]?.macAddress &&
                  deviceMappings[selectedRoom.roomNumber]?.macAddress !== 'N/A' &&
                  selectedRoom.guestDetails && (
                    <DetailsSection>
                      <DetailsSectionTitle>Health Data Period</DetailsSectionTitle>
                      <DataCard>
                        <DataRow>
                          <DataLabel>From</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.checkInDate}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>To</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.checkOutDate}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Status</DataLabel>
                          <DataValue>{loadingData ? 'Loading data...' : 'Data loaded'}</DataValue>
                        </DataRow>
                      </DataCard>
                    </DetailsSection>
                  )}

                {/* 健康數據圖表 */}
                {deviceMappings[selectedRoom.roomNumber]?.macAddress &&
                  deviceMappings[selectedRoom.roomNumber]?.macAddress !== 'N/A' && (
                    <>
                      {filteredHrData.length > 0 ? (
                        <>
                          <ChartContainer style={{ height: '200px' }}>
                            <ChartTitle>Heart Rate</ChartTitle>
                            <Line data={heartRateData} options={chartOptions} />
                          </ChartContainer>

                          <ChartContainer style={{ height: '200px' }}>
                            <ChartTitle>Breath Rate</ChartTitle>
                            <Line data={breathRateData} options={chartOptions} />
                          </ChartContainer>

                          <RelaxationScoreContainer>
                            <RelaxationScoreTitle>Relaxation Score</RelaxationScoreTitle>
                            <RelaxationScore>{relaxationScore || "N/A"}</RelaxationScore>
                          </RelaxationScoreContainer>
                        </>
                      ) : loadingData ? (
                        <DataCard>
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <div>Loading health data...</div>
                          </div>
                        </DataCard>
                      ) : (
                        <DataCard>
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <div>No health data available for this room.</div>
                            <div style={{ fontSize: '0.8rem', marginTop: '10px', color: '#8aa0c1' }}>
                              Health data is collected during guest stays and may not be available for all rooms.
                            </div>
                          </div>
                        </DataCard>
                      )}
                    </>
                  )}

                {selectedRoom.guestDetails ? (
                  <>
                    <DetailsSection>
                      <DetailsSectionTitle>Guest Information</DetailsSectionTitle>
                      <DataCard>
                        <DataRow>
                          <DataLabel>Guest Name</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.guestName}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Nationality</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.nationality?.split('|')[0]}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Mobile Number</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.mobileNumber}</DataValue>
                        </DataRow>
                      </DataCard>
                    </DetailsSection>

                    <DetailsSection>
                      <DetailsSectionTitle>Stay Information</DetailsSectionTitle>
                      <DataCard>
                        <DataRow>
                          <DataLabel>Check-in Date</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.checkInDate}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Check-out Date</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.checkOutDate}</DataValue>
                        </DataRow>
                        <DataRow>
                          <DataLabel>Remarks</DataLabel>
                          <DataValue>{selectedRoom.guestDetails.remarks || 'N/A'}</DataValue>
                        </DataRow>
                      </DataCard>
                    </DetailsSection>
                  </>
                ) : (
                  <DetailsSection>
                    <DetailsSectionTitle>Vacancy Information</DetailsSectionTitle>
                    <DataCard>
                      <DataRow>
                        <DataLabel>Status</DataLabel>
                        <DataValue>Available for booking</DataValue>
                      </DataRow>
                      <DataRow>
                        <DataLabel>Last Occupied</DataLabel>
                        <DataValue>N/A</DataValue>
                      </DataRow>
                    </DataCard>
                  </DetailsSection>
                )}

                {/* 睡眠報告生成器 */}
                {selectedRoom.guestDetails && deviceMappings[selectedRoom.roomNumber]?.macAddress && (
                  <DetailsSection>
                    <DetailsSectionTitle>Sleep Report</DetailsSectionTitle>
                    <DataCard>
                      <DataRow>
                        <DataLabel>Sleep Data</DataLabel>
                        <DataValue>
                          {loadingData ? 'Loading data...' :
                            (filteredHrData.length > 0 || filteredRrData.length > 0) ?
                              'Available' : 'No data available'}
                        </DataValue>
                      </DataRow>

                      {!showSleepReport && !loadingData && (filteredHrData.length > 0 || filteredRrData.length > 0) && (
                        <SleepReportButton
                          primary
                          onClick={prepareSleepReport}
                          style={{ marginTop: '10px' }}
                        >
                          Generate Sleep Report
                        </SleepReportButton>
                      )}

                      {showSleepReport && (
                        <SleepReportSection>
                          <ReportLabel>Guest Name:</ReportLabel>
                          <UserNameInput
                            type="text"
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            placeholder="Enter guest name"
                          />

                          <ReportPreview>
                            <ReportTextArea
                              value={sleepReport}
                              onChange={(e) => setSleepReport(e.target.value)}
                              readOnly={false}
                            />
                            <CopyButton
                              onClick={copyReportToClipboard}
                              copied={reportCopied}
                            >
                              {reportCopied ? 'Copied!' : 'Copy Report'}
                            </CopyButton>
                          </ReportPreview>

                          <SleepReportButton
                            onClick={() => setShowSleepReport(false)}
                            style={{ marginTop: '10px' }}
                          >
                            Close Report
                          </SleepReportButton>
                        </SleepReportSection>
                      )}
                    </DataCard>
                  </DetailsSection>
                )}
              </RoomDetails>
            )}
          </DetailsPanel>
        </Main>
      </Container>
    </>
  );
};

export default HotelRoomStatusPage;